import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import TextField from "@mui/material/TextField";
import {
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';

import style from "./ProfileForm.module.css";

import { usePromptOnLeave } from '../../hooks/usePromptOnLeave';

const ProfileForm = ({ onSave, onReset, initialUser }) => {
  const { t } = useTranslation();
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [user, setUser] = useState(null);
  const [errors, setErrors] = useState({ name: "", email: "" });

  const { showModal, onConfirm, onCancel } = usePromptOnLeave(
    t('common.unsaved_changes'),
    isSaveEnabled
  );

  useEffect(() => {
    if (initialUser) {
      setUser(initialUser);
    }
  }, [initialUser]);

  if (!user) return null;

  const validateFields = () => {
    let newErrors = {};

    if (!user.full_name.trim()) newErrors.full_name = t('common.required');
    if (!user.email.trim()) {
      newErrors.email = t('common.required');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(user.email)) {
        newErrors.email = t('profile.invalid_email');
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSaveChanges = () => {
    if (validateFields()) {
      onSave(user);
      setIsSaveEnabled(false);
    }
  };

  const handleResetChanges = () => {
    setUser(initialUser);
    setIsSaveEnabled(false);
    setErrors({});
    onReset();
  };

  const handleInputChange = (field, value) => {
    setUser((prevUser) => ({
      ...prevUser,
      [field]: value,
    }));
    setIsSaveEnabled(true);
    setErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  return (
    <div className={style.form}>
      <TextField
        className={style.input}
        variant="outlined"
        fullWidth
        value={user.full_name}
        label={t('profile.name_label')}
        placeholder={t('profile.name_label')}
        margin="normal"
        required
        error={!!errors.full_name}
        helperText={errors.full_name}
        onChange={(e) => handleInputChange("full_name", e.target.value)}
      />
      <TextField
        className={style.input}
        variant="outlined"
        fullWidth
        value={user.email}
        type="email"
        label={t('common.email')}
        placeholder={t('common.email')}
        margin="normal"
        required
        error={!!errors.email}
        helperText={errors.email}
        onChange={(e) => handleInputChange("email", e.target.value)}
      />
      <Link to="/reset-password" className={`button ${style.changePasswordButton}`}>{t('profile.change_password')}</Link>
      <div className={style.buttons}>
        <button
          onClick={handleResetChanges}
          disabled={!isSaveEnabled}
          className={`button thirdly-button ${style.cancelButton}`}
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSaveChanges}
          disabled={!isSaveEnabled}
          className={`button ${style.saveButton}`}
        >
          {t('common.save')}
        </button>
      </div>
      <Dialog
        open={showModal}
        onClose={onCancel}
        aria-labelledby="unsaved-changes-title"
        aria-describedby="unsaved-changes-description"
      >
        <DialogContent>
          <p sx={{
            padding: "30px 25px",
            fontFamily: "Inerta",
          }}>
            {t('common.unsaved_changes')}
          </p>
        </DialogContent>
        <DialogActions sx={{padding: "20px"}}>
          <button onClick={onCancel} className="button secondary-button cancelButton">
            {t('common.cancel_short')}
          </button>
          <button onClick={onConfirm} className="button primary-button saveButton">
            {t('common.leave')}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProfileForm;
